import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import useSWR from 'swr';
import { handleApiError } from 'utils/errorHandler';
import { fetcherWithMethod } from 'utils/axios';
import useAuth from 'hooks/useAuth';
import { AuthUserPermissionsResponse } from 'types/auth';

interface PermissionContextType {
    permissions: AuthUserPermissionsResponse;
    isLoading: boolean;
    error: any;
    mutate: () => void;
    hasError: boolean;
    retry: () => void;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
    const { isLoggedIn } = useAuth();
    const endpoint = 'UserManagement/AuthorizeUser/GetPermissions';

    const swrKey = isLoggedIn ? [endpoint, { method: 'GET' }] : null;

    const { data, error, isLoading, mutate } = useSWR<AuthUserPermissionsResponse>(
        swrKey,
        (args: [string, any]) => fetcherWithMethod('GET', args),
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            revalidateOnMount: true,
            onError: handleApiError,
            dedupingInterval: 60000 // Dedupe for 1 minute
        }
    );

    const value = useMemo(() => ({
        permissions: data ?? [],
        isLoading: isLoggedIn ? isLoading : false,
        error,
        mutate: () => mutate(),
        hasError: !!error,
        retry: () => mutate()
    }), [data, error, isLoading, isLoggedIn, mutate]);

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissionContext = () => {
    const context = useContext(PermissionContext);
    if (context === undefined) {
        throw new Error('usePermissionContext must be used within a PermissionProvider');
    }
    return context;
};
